import { Loadable } from '@/components/loaders';
import RouteConstants from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { PageLayout } from '@/global/components';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { BreadcrumbItem, Breadcrumbs } from '@/lib/Breadcrumbs';
import { TabItem, Tabs } from '@/lib/Tabs';
import { DataSource, DataSourceCodeConfig, DataSourceFileConfig, EDataSourceCategory, EDataSourceStatus, TDataSource } from '@/models/dataSource';
import { useGetDataSourceQuery, useGetDataSourceStatusQuery } from '@/services/dataSources';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CodeDataSourceData, DataSourceOverview, FileDataSourceData } from './components';
import { DataSourceDetailHeader } from './components/DataSourceDetailHeader';
import { POLLING_INTERVAL } from './components/tabs/overview/constants';

const breadcrumbs: BreadcrumbItem[] = [
  {
    label: 'ALL:data_sources.breadcrumb',
    path: RouteConstants.dataSources,
  },
  {
    label: 'ALL:data_sources.myDataSources',
  },
];

const isOfTypeSourceConnector = (dataSource: DataSource) => dataSource?.type?.category === EDataSourceCategory.SOURCE_CONNECTOR;

export const DataSourceDetails: FC = () => {
  const { id: dataSourceId } = useParams<{ id: string }>();
  const history = useHistory();

  const workspace = useWorkspace();

  const [pollingInterval, setPollingInterval] = useState(POLLING_INTERVAL);

  const { data } = useGetDataSourceStatusQuery(
    {
      workspaceId: workspace.id,
      dataSourceId: dataSourceId,
    },
    {
      skip: !dataSourceId,
      pollingInterval,
    }
  );

  useEffect(() => {
    if (data?.status === EDataSourceStatus.ACTIVE) {
      setPollingInterval(0);
    }
  }, [data]);

  const {
    isFetching,
    data: dataSource,
    isUninitialized,
    isError,
    error,
  } = useGetDataSourceQuery({
    workspaceId: workspace.id,
    dataSourceId,
  });

  useEffect(() => {
    if (isError && error && 'status' in error && error.status === 404) {
      history.replace(RouteConstants.dataSources);
    }
  }, [error, history, isError]);

  const getDataTab = useCallback(() => {
    switch (dataSource?.type.category) {
      case EDataSourceCategory.FILE: {
        return <FileDataSourceData dataSource={dataSource as TDataSource<DataSourceFileConfig>} />;
      }

      case EDataSourceCategory.CODE:
        return <CodeDataSourceData dataSource={dataSource as TDataSource<DataSourceCodeConfig>} />;

      default:
        return null;
    }
  }, [dataSource?.type.category]);

  const dataSourceIsActive = useMemo(
    () => dataSource?.status === EDataSourceStatus.ACTIVE || data?.status === EDataSourceStatus.ACTIVE,
    [dataSource?.status, data?.status]
  );

  const DATASOURCE_DETAIL_TAB_ITEMS: TabItem[] = useMemo(() => {
    const tabs: TabItem[] = [
      {
        title: 'data_source_details.tabs.overview',
        element: <DataSourceOverview dataSource={dataSource} isActive={dataSourceIsActive} />,
      },
    ];

    if (!isOfTypeSourceConnector(dataSource) && dataSourceIsActive) {
      tabs.push({
        title: 'data_source_details.tabs.data',
        element: getDataTab(),
      });
    }

    return tabs;
  }, [dataSource, dataSourceIsActive, getDataTab]);

  const isFetchingDelayed = useDelayedBoolean(isFetching, 400);

  const shouldShowLoading = isFetching || isFetchingDelayed || isUninitialized;

  return (
    <Loadable isLoading={shouldShowLoading}>
      <PageLayout className='gap-0 bg-secondary-background px-0'>
        <div className='bg-white-100 px-8'>
          <Breadcrumbs className='border-b border-quaternary-200 pb-4' items={breadcrumbs} />
        </div>
        <DataSourceDetailHeader dataSource={dataSource} isActive={dataSourceIsActive} />
        <Tabs classNames={{ nav: 'bg-white-100 pt-8 px-8', tabWrapper: 'px-8' }} items={DATASOURCE_DETAIL_TAB_ITEMS} disabled={!dataSourceIsActive} />
      </PageLayout>
    </Loadable>
  );
};
